import { appState, confirmationAlertProps, customModalProps } from '../../../../interfaces/common';
import {
  AddFeedbackFormData,
  FeedbackConfigPayload,
  FeedbackIdPayload,
  FeedbackLanguagePayload,
  FeedbackWebhookPayload,
  SendFeedbackPayload,
} from '../../../../interfaces/customer-simulator';
import {
  Button,
  Card,
  ColumnProps,
  Flex,
  Icon,
  ModalManagerContext,
  ModalMethods,
  Text,
  Grid,
  Input,
  Switch,
  Link,
} from '@pega/cosmos-react-core';
import React, { Dispatch, SetStateAction, useContext, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import useLocalTranslation from '../../../../hooks/useLocalTranslation';
import { updateBannerInfo, updateLoaderDetails } from '../../../../redux/actions';
import { StyledTable } from '../../../Utilities/custom.styled';
import { ConfirmationAlert, CustomModal } from '../../../Utilities/CustomModal';
import { genAiUpdateFeedbackData } from '../AISimulatorApis';
import ActionsPopover from './ActionsPopover';
import AddFeedback from '../AddFeedback';
import { ActionsPopoverFlow } from '../../../../constants/common';
import CustomLabel from '../../../Utilities/CustomLabel';
import { StyledLinkHover, linkTextStyle } from '../../../Utilities/MultiLocaleTitleAndSpecialNote';
import TagSnippet from '../../Chat/Modals/TagSnippet';
import EditWebhookModal, { EditWebhookProps, isValidUrl } from '../../../Utilities/EditWebhookModal';
import DisplayWebhooks from '../../../Utilities/DisplayWebhooks';

export interface SimulatorConfigurationProps {
  connectionId: string;
  accountDetails: any;
  setAccountDetails: Dispatch<SetStateAction<any>>;
}

const AgentFeedback: React.FC<SimulatorConfigurationProps> = ({ connectionId, accountDetails, setAccountDetails }) => {
  const translate = useLocalTranslation();
  const dispatch = useDispatch();
  const {
    simulationStatus,
    enableSendAgentFeedback,
    feedbackLanguage,
    feedbackData,
    feedbackWebhooks,
  } = accountDetails;
  const sessionId = useSelector((state: appState) => state.sessionId);
  const customModalMethods = useRef<ModalMethods<Partial<customModalProps>>>();
  const confirmationModalMethods = useRef<ModalMethods<Partial<confirmationAlertProps>>>();
  const { create } = useContext(ModalManagerContext);
  const [selectedLanguage, setSelectedLanguage] = useState(feedbackLanguage);
  const [webhook, setWebhook] = useState('');
  const modalMethods = useRef<ModalMethods<Partial<customModalProps>>>();
  const deleteModalMethods = useRef<ModalMethods<Partial<confirmationAlertProps>>>();
  const editModalMethods = useRef<ModalMethods<Partial<EditWebhookProps>>>();
  const disableAction = simulationStatus === 'start';
  const [webhookError, setWebhookError] = useState('');
  const updatedWebhookList = JSON.parse(JSON.stringify(feedbackWebhooks || []));

  const updateSelectedLanguage = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedLang = e.target.value;
    setSelectedLanguage(selectedLang);
  };

  const updateWebhook = (e: React.ChangeEvent<HTMLInputElement>) => {
    const webhook = e.target.value;
    setWebhook(webhook);
  };

  const apiCallHandler = async (
    payload:
      | FeedbackConfigPayload
      | FeedbackIdPayload
      | SendFeedbackPayload
      | FeedbackLanguagePayload
      | FeedbackWebhookPayload,
    partial: string,
  ) => {
    dispatch(updateLoaderDetails(true));
    const response = await genAiUpdateFeedbackData(sessionId, connectionId, payload, partial);
    if (response?.status === 200) {
      setAccountDetails(response?.data);
      dispatch(updateBannerInfo(translate(`${partial}_success`), 'success'));
      dispatch(updateLoaderDetails(false));
    } else {
      dispatch(
        updateBannerInfo(
          response?.errorMessage ? translate(response?.errorMessage) : translate(`${partial}_failed`),
          'urgent',
        ),
      );
      dispatch(updateLoaderDetails(false));
    }
  };

  const saveSelectedLanguage = () => {
    const payload = {
      feedbackLanguage: selectedLanguage,
    };
    apiCallHandler(payload, 'feedback_language');
  };

  const updateSendAgentFeedback = async (e: any) => {
    const checked = e.target.checked;
    const payload = {
      enableSendAgentFeedback: checked,
    };
    apiCallHandler(payload, 'enable_agent_feedback');
  };

  const onAddFeedback = (data: AddFeedbackFormData) => {
    const payload = {
      feedback: data,
    };
    apiCallHandler(payload, 'add_feedback');
  };

  const deleteFeedback = (feedbackId: any) => {
    const payload = {
      feedbackId: feedbackId,
    };
    apiCallHandler(payload, 'delete_feedback');
  };

  const onEditFeedback = (data: AddFeedbackFormData) => {
    const payload = {
      feedback: data,
    };
    apiCallHandler(payload, 'edit_feedback');
  };

  const deleteConfirmation = (id: string, name: string) => {
    confirmationModalMethods.current = create(
      ConfirmationAlert,
      {
        pageName: name,
        deleteChannel: () => deleteFeedback(id),
      },
      { alert: true },
    );
  };

  const handleEditFeedback = (feedback: any) => {
    customModalMethods.current = create(CustomModal, {
      heading: translate('edit_feedback'),
      children: <AddFeedback handleFormSubmit={onEditFeedback} feedback={feedback} />,
    });
  };

  const handleAddFeedback = () => {
    customModalMethods.current = create(CustomModal, {
      heading: translate('add_feedback'),
      children: <AddFeedback handleFormSubmit={onAddFeedback} />,
    });
  };

  const data = feedbackData?.map((item: any, index: any) => {
    const { name, responseType, description } = item;
    return {
      id: index,
      name: <Text>{name}</Text>,
      responseType: <Text>{responseType}</Text>,
      description: <Text>{description}</Text>,
      actions: (
        <>
          <ActionsPopover
            flow={ActionsPopoverFlow.Feedback}
            scenario={item}
            simulationStatus={simulationStatus}
            handleEditScenario={handleEditFeedback}
            deleteConfirmation={deleteConfirmation}
          />
        </>
      ),
    };
  });

  const columns: ColumnProps<typeof data[number]>[] = [
    { renderer: 'name', label: translate('name') },
    { renderer: 'responseType', label: translate('response_type') },
    { renderer: 'description', label: translate('description') },
    { renderer: 'actions', label: '' },
  ];

  const showPayload = () => {
    modalMethods.current = create<customModalProps>(CustomModal, {
      heading: translate('feedback_webhook_payload_header'),
      children: <TagSnippet currentTab="genAIFeedback" data={feedbackData} />,
    });
  };

  const addFeedbackWebhook = async () => {
    if (isValidUrl(webhook) && !feedbackWebhooks?.includes(webhook)) {
      updatedWebhookList.push(webhook.trim());
      const payload = {
        webhooks: updatedWebhookList,
      };
      apiCallHandler(payload, 'add_webhook');
      setWebhook('');
      setWebhookError('');
    } else {
      setWebhookError(feedbackWebhooks?.includes(webhook) ? translate('existing_error') : translate('invalid_webhook'));
    }
  };

  const deleteFeedbackWebhook = async (webhookToBeDeleted?: string) => {
    const updatedList = updatedWebhookList.filter((val: string) => val !== webhookToBeDeleted);
    const payload = {
      webhooks: updatedList,
    };
    apiCallHandler(payload, 'delete_webhook');
  };

  const deleteWebhookConfirmation = (index: number) => {
    deleteModalMethods.current = create(
      ConfirmationAlert,
      { isErroWebhook: true, webhookToBeDeleted: feedbackWebhooks[index], deleteChannel: deleteFeedbackWebhook },
      { alert: true },
    );
  };

  const onEditFeedbackWebhook = async (updatedWebhook: string, index: number) => {
    updatedWebhookList[index] = updatedWebhook;
    const payload = {
      webhooks: updatedWebhookList,
    };
    apiCallHandler(payload, 'edit_webhook');
  };

  const editWebhook = (index: number) => {
    editModalMethods.current = create(
      EditWebhookModal,
      {
        index,
        selectedWebhook: feedbackWebhooks[index],
        updateWebhook: onEditFeedbackWebhook,
        webhooks: feedbackWebhooks,
      },
      { alert: true },
    );
  };

  return (
    <Flex
      container={{
        colGap: 2,
        direction: 'column',
        gap: 2,
      }}
    >
      <Grid
        container={{
          cols: 'repeat(3, 30% 56% 10%)',
          gap: 2,
          alignItems: 'center',
        }}
      >
        <CustomLabel label={translate('feedback_language')} />
        <Input
          name="feedbackLanguage"
          label={translate('feedback_language')}
          labelHidden={true}
          autoComplete="off"
          value={selectedLanguage}
          onChange={updateSelectedLanguage}
          disabled={disableAction}
        />
        <Button
          variant="primary"
          onClick={saveSelectedLanguage}
          disabled={disableAction || !selectedLanguage?.trim() || selectedLanguage === feedbackLanguage}
        >
          {translate('save')}
        </Button>
      </Grid>
      <Grid
        container={{
          cols: 'repeat(2, 30% 60%)',
          gap: 1,
          alignItems: 'center',
        }}
      >
        <CustomLabel label={translate('send_agent_feedback')} />
        <Flex container={{ alignItems: 'start' }}>
          <Switch
            id="force_escalation"
            name="isEnable"
            onChange={updateSendAgentFeedback}
            on={enableSendAgentFeedback}
            disabled={disableAction}
          />
        </Flex>
      </Grid>
      <Grid
        container={{
          cols: 'repeat(3, 30% 56% 10%)',
          gap: 2,
          alignItems: 'center',
        }}
      >
        <Flex>
          <CustomLabel label={translate('feedback_webhook')} />
          {feedbackWebhooks?.length > 0 && (
            <Link href="" as={StyledLinkHover} style={linkTextStyle} onClick={showPayload}>
              {'Show payload'}
            </Link>
          )}
        </Flex>
        <Input
          label={translate('feedback_webhook')}
          type="text"
          autoComplete="off"
          value={webhook}
          onChange={updateWebhook}
          info={webhookError}
          required={true}
          labelHidden={true}
          status={webhookError ? 'error' : undefined}
          disabled={disableAction && feedbackData.length === 10}
        />

        <Button variant="primary" onClick={addFeedbackWebhook} disabled={disableAction || !webhook?.trim()}>
          {translate('add')}
        </Button>
      </Grid>
      {feedbackWebhooks?.length > 0 && (
        <DisplayWebhooks
          webhooks={feedbackWebhooks}
          editWebhook={editWebhook}
          deleteConfirmation={deleteWebhookConfirmation}
        />
      )}
      <Card style={{ padding: 0 }} groupId="popover">
        <StyledTable hoverHighlight={true} padding={'default'} columns={columns} data={data} />
      </Card>
      <Flex>
        <Button variant="secondary" onClick={handleAddFeedback} disabled={disableAction || feedbackData?.length >= 10}>
          <Icon name="plus" style={{ height: '1rem', width: '1rem', marginRight: 5 }} />
          {translate('add_feedback')}
        </Button>
      </Flex>
    </Flex>
  );
};

export default AgentFeedback;
